import { Colors, Lightning } from "@lightningjs/sdk";
import { SeparatedText } from "../SeparatedText/SeparatedText";
import { BrandingModel, MediaModel } from "../../../lib/models";
import { MediaDynamicBanner } from "../../../pages/vod/components/MediaDynamicBanner/MediaDynamicBanner";
import { MovieName } from "../../../pages/vod/components/MovieName/MovieName";
import { Badge } from "../Badge/Badge";
import { MediaDurationWithProgress } from "../../../pages/vod/components/MediaDurationWithProgress/MediaDurationWithProgress";
import { Button } from "../../input/Button/Button";
import theme from "../../../lib/theme";
import { Theme, themeManager } from "../../configurations/Theme/ThemeManager";
import { MENU_WIDTH } from "../../../lib/utils";

interface MovieDetailsTemplateSpec extends Lightning.Component.TemplateSpec {
    maxW: number;
    data: MediaModel | BrandingModel;
    Background: typeof MediaDynamicBanner;
    Content: {
        ContentLeft: {
            SeriesName: object;
            Name: typeof MovieName;
            Labels: {
                Badge: typeof Badge;
                Year: typeof Object;
                Rating: typeof Object;
                Age: typeof SeparatedText;
                Time: typeof MediaDurationWithProgress;
                Separator: typeof SeparatedText;
            };
            Description: object;
            Program: {
                ProgramTitle: object;
                ProgressBar: { Fill: object };
                ProgramTime: object;
            };
            Link: typeof Button;
        };
        ContentRight: object;
    };
}

export class MovieDetails
    extends Lightning.Component<MovieDetailsTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<MovieDetailsTemplateSpec>
{
    _boundEventHandler?: any;
    _isBranding = false;
    _data!: MediaModel | BrandingModel;
    private _themeCallback!: (theme: Theme | undefined) => void;

    static override _template(): Lightning.Component.Template<MovieDetailsTemplateSpec> {
        return {
            x: 0,
            y: 0,
            w: theme.banner.w - MENU_WIDTH,
            h: theme.banner.h,
            Background: {
                type: MediaDynamicBanner
            },
            Content: {
                w: (w) => w - theme.banner.padding.x,
                h: (h) => h - theme.banner.padding.x,
                x: theme.banner.padding.x,
                y: theme.banner.padding.y || theme.banner.padding.x,
                flex: { direction: "row" },
                ContentLeft: {
                    w: theme.banner.content.w,
                    flex: { direction: "column", justifyContent: "flex-start" },
                    SeriesName: {
                        text: {
                            text: "",
                            fontSize: theme.banner.meta.font.size + 4,
                            fontFace: "Medium",
                            wordWrap: true,
                            lineHeight: 24,
                            wordWrapWidth: 1400,
                            maxLines: 3,
                            textOverflow: "ellipsis"
                        }
                    },
                    Name: {
                        maxW: theme.banner.content.w,
                        type: MovieName,
                        flexItem: { marginBottom: theme.banner.items.offset.x + 4 }
                    },
                    Labels: {
                        alpha: 0,
                        flex: { direction: "row" },
                        flexItem: { marginBottom: theme.banner.items.offset.x },
                        Year: {
                            text: {
                                text: "",
                                fontSize: theme.banner.meta.font.size
                            }
                        },
                        Rating: {
                            text: {
                                text: "",
                                fontSize: theme.banner.meta.font.size
                            }
                        },
                        Time: {
                            type: MediaDurationWithProgress,
                            fontSize: theme.banner.meta.font.size
                        }
                    },
                    Description: {
                        h: 150,
                        text: {
                            text: "",
                            fontSize: theme.banner.description.font.size,
                            fontFace: theme.banner.description.font.face,
                            wordWrap: true,
                            lineHeight: theme.banner.description.font.lineHeight,
                            wordWrapWidth: theme.banner.content.w,
                            maxLines: theme.banner.description.maxLines,
                            textOverflow: "ellipsis"
                        }
                    },
                    Program: {
                        alpha: 0,
                        flex: { direction: "column" },
                        ProgramTitle: {
                            text: {
                                text: "",
                                fontSize: theme.banner.description.font.size + 2,
                                fontFace: "Medium",
                                wordWrap: true,
                                wordWrapWidth: theme.banner.content.w,
                                lineHeight: theme.banner.description.font.lineHeight + 4,
                                maxLines: 2,
                                textOverflow: "ellipsis"
                            }
                        },
                        ProgressBar: {
                            flexItem: { marginTop: 18 },
                            w: 480,
                            h: 8,
                            rect: true,
                            color: Colors(theme.colors.bg.secondary).alpha(0.8).get(),
                            shader: { type: Lightning.shaders.RoundedRectangle, radius: 4 },
                            Fill: {
                                w: 0,
                                h: 8,
                                rect: true,
                                color: Colors(theme.colors.focus).get(),
                                shader: { type: Lightning.shaders.RoundedRectangle, radius: 4 }
                            }
                        },
                        ProgramTime: {
                            flexItem: { marginTop: 12 },
                            text: {
                                text: "",
                                fontSize: theme.banner.meta.font.size,
                                fontFace: "Secondary-Regular",
                                textColor: Colors(theme.colors.text.secondary).get()
                            }
                        }
                    },
                    Link: {
                        type: Button,
                        label: "general.watch",
                        alpha: 0,
                        w: 156
                    }
                },
                ContentRight: {}
            }
        };
    }

    readonly Background = this.getByRef("Background")!;
    readonly Content = this.getByRef("Content")!;
    readonly ContentLeft = this.Content.getByRef("ContentLeft")!;
    readonly SeriesName = this.ContentLeft.getByRef("SeriesName")!;
    readonly Name = this.ContentLeft.getByRef("Name")!;
    readonly Labels = this.ContentLeft.getByRef("Labels")!;
    readonly Description = this.ContentLeft.getByRef("Description")!;
    readonly Program = this.ContentLeft.getByRef("Program")!;
    readonly ProgramTitle = this.Program.getByRef("ProgramTitle")!;
    readonly ProgramBar = this.Program.getByRef("ProgressBar")!;
    readonly ProgramFill = this.ProgramBar.getByRef("Fill")!;
    readonly ProgramTime = this.Program.getByRef("ProgramTime")!;
    readonly Time = this.Labels.getByRef("Time")!;
    readonly Year = this.Labels.getByRef("Year")!;
    readonly Rating = this.Labels.getByRef("Rating")!;
    readonly Link = this.ContentLeft.getByRef("Link")!;

    override _setup() {
        this._boundEventHandler = this._update.bind(this);
    }
    override _detach() {
        themeManager.unsubscribe(this._themeCallback);
        this.application.off("updateSelectedMovie", this._boundEventHandler);
        super._detach();
    }
    override _init() {
        this._themeCallback = this._onThemeChange.bind(this);
        themeManager.subscribe(this._themeCallback);
    }

    private _onThemeChange(customTheme: Theme | undefined): void {
        this.SeriesName.patch({
            text: {
                textColor: customTheme?.paragraph ?? Colors(theme.colors.text.primary).get()
            }
        });
        this.Name.patch({
            text: {
                textColor: customTheme?.title ?? Colors(theme.colors.text.primary).get()
            }
        });
        this.Description.patch({
            text: {
                textColor: customTheme?.paragraph ?? Colors(theme.colors.text.primary).get()
            }
        });
        this.Link.patch({
            text: {
                textColor: customTheme?.title ?? Colors(theme.colors.text.primary).get()
            }
        });
    }

    override _active() {
        this.application.on("updateSelectedMovie", this._boundEventHandler);
    }

    override _inactive() {
        this.application.off("updateSelectedMovie", this._boundEventHandler);
    }

    override _getFocused() {
        return this.Link;
    }

    _update(data: MediaModel | BrandingModel) {
        // Live-TV channels: show the current program (title + progress + time)
        // in the header, mirroring the Live TV page channel item.
        if ((data as any).__typename === "ChannelContent") {
            this.Name.patch({ text: data.title || "" });
            this.SeriesName.patch({ text: { text: "" } });
            this.Labels.patch({ alpha: 0 });
            this.Description.patch({ alpha: 0 });
            this.Link.patch({ alpha: 0 });
            this._renderProgram((data as any).currentProgram);
            return;
        }

        this.Program.patch({ alpha: 0 });
        this.Description.patch({ alpha: 1 });

        if (!this._isBranding || "releaseYear" in data) {
            const hasDuration = (data as MediaModel).duration;
            const rating = (data as MediaModel).fsk;
            const year = (data as MediaModel).releaseYear?.toString() || "";

            this.Year.patch({
                text: {
                    text: year + (year && (rating || hasDuration) ? "  •" : "")
                },
                flexItem: { marginRight: !!year ? 10 : 0 }
            });

            this.Rating.patch({
                text: {
                    text: rating ? (rating.toString() + "+" + (hasDuration ? "  •" : "")) : ""
                },
                flexItem: { marginRight: rating ? 10 : 0 }
            });

            this.Time.patch({
                duration: (data as MediaModel).duration,
                progress: (data as MediaModel).progress?.current
            });

            this.SeriesName.patch({
                text: (data as MediaModel).season?.series?.title || (data as MediaModel)?.series?.title || ""
            });
        } else {
            this.Link.patch({
                alpha: !!(data as BrandingModel).movieId ? 1 : 0
            });
        }

        this.Description.patch({ text: data.description || "" });
        this.Name.patch({ text: data.title || "" });

        this.Labels.patch({
            alpha: 1
        });
    }

    private _renderProgram(program: any) {
        if (!program?.start || !program?.stop) {
            this.Program.patch({ alpha: 0 });
            return;
        }

        const start = Date.parse(program.start);
        const end = Date.parse(program.stop);
        const now = Date.now();
        const isAiring = !!start && !!end && now >= start && now < end;
        const progress = isAiring && end > start ? Math.min(1, Math.max(0, (now - start) / (end - start))) : 0;
        const barW = 480;

        this.ProgramTitle.patch({ text: { text: program.title || "" } });
        this.ProgramFill.patch({ w: barW * progress });
        this.ProgramTime.patch({
            text: { text: `${this._fmtTime(program.start)} - ${this._fmtTime(program.stop)}` }
        });
        this.Program.patch({ alpha: 1 });
    }

    private _fmtTime(dateString: string): string {
        return new Date(dateString).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }

    set maxW(value: number) {
        this.Name.patch({
            maxW: value
        });
    }

    set data(value: MediaModel | BrandingModel) {
        this._data = value;

        this._update(value);
    }
}
